import { useToggle } from '@react-hookz/web/useToggle/index.js';

import { ArticleType } from '#components/Article/ArticleType';
import { IconSystemButton } from '#components/base/IconButton/IconSystemButton';
import { getCopyrightInformation } from '#cutils/articles/getCopyrightInformation';
import { MediaContextPlaybackStatus } from '#cutils/context/MediaContext';
import { useBreakpoint } from '#cutils/hooks/useBreakpoint';
import { useElementOverflow } from '#cutils/hooks/useElementOverflow';
import type { ModuleType } from '#types/global';

import styles from './ArticleModuleMediaCollapsibleMetadataBox.module.scss';
import type { ArticleModuleHeroSidebarSectionCopyrightFragment } from './__generated__/ArticleModuleHeroSidebarSectionCopyright.generated';

type Props = {
	articleModule: ArticleModuleHeroSidebarSectionCopyrightFragment;
	className?: string;
	mediaPlaybackStatus: MediaContextPlaybackStatus;
	moduleType?: ModuleType | null;
};

export function ArticleModuleMediaCollapsibleMetadataBox({ articleModule, className = '', mediaPlaybackStatus, moduleType }: Props) {
	const { copyright: mediaCopyright, text: captionText, thumbnailCopyright, thumbnailText } = getCopyrightInformation(articleModule);

	const isPlayingOrPaused =
		mediaPlaybackStatus === MediaContextPlaybackStatus.PAUSED || mediaPlaybackStatus === MediaContextPlaybackStatus.STARTED;

	const imageCopyright = thumbnailCopyright ? `Bildrechte: ${thumbnailCopyright}` : null;
	const avCopyright = mediaCopyright ? `© ${mediaCopyright}` : null;

	const copyright = isPlayingOrPaused ? avCopyright : imageCopyright;
	const caption = isPlayingOrPaused ? captionText : thumbnailText;

	const [isExpanded, toggleIsExpanded] = useToggle(false);

	const captionDefined = !!caption && !!caption.trim();

	const expandButtonVisible = !useBreakpoint('lg');

	// if no caption is defined and the expand button is visible, detect if the copyright text overflows
	// if it overflows show the expand button, otherwise hide the button
	const detectCopyrightOverflow = !captionDefined && expandButtonVisible;

	const { ref, overflow } = useElementOverflow('horizontal', detectCopyrightOverflow);

	return (
		<div className={`body3 ${styles.wrapper} ${className} ${isExpanded ? styles.expanded : ''}`} onClick={toggleIsExpanded}>
			<IconSystemButton
				type="tertiary"
				size="s"
				icon="chevron-down"
				variant="filled"
				className={`${styles.collapseButton} ${overflow || isExpanded || !detectCopyrightOverflow ? '' : styles.hidden}`}
				aria-label={isExpanded ? 'Zusätzliche Informationen ausblenden' : 'Zusätzliche Informationen anzeigen'}
				aria-expanded={isExpanded}
			/>
			<span ref={ref} className={styles.copyright}>
				{copyright}
			</span>

			<ArticleType className={`${styles.moduleType} body6`} moduleType={moduleType} iconSize="xs" />

			{captionDefined ? <p className={styles.caption}>{caption}</p> : null}
		</div>
	);
}
